<template lang="pug">
.cara-page
  //- Page Header
  .page-header.mb-4.d-flex.justify-content-between.align-items-center
    h3.page-title.mb-0
      Icon.mr-2(icon="mdi:card-account-details")
      | CARA Membership
    div
      b-button.mr-2(variant="outline-success" @click="exportExcel" :disabled="loading || exporting || exportingStatuses")
        b-spinner(small v-if="exporting")
        Icon(v-else icon="tabler:file-spreadsheet")
        |  Export Registrations
      b-button.mr-2(variant="outline-info" @click="exportStatuses" :disabled="loading || exporting || exportingStatuses")
        b-spinner(small v-if="exportingStatuses")
        Icon(v-else icon="tabler:list-check")
        |  Export Statuses
      b-button(variant="primary" @click="fetchRecords" :disabled="loading")
        b-spinner(small v-if="loading")
        Icon(v-else icon="tabler:refresh")
        |  Refresh

  //- Summary chips
  b-row.mb-3
    b-col(cols="6" md="3" v-for="s in statusSummary" :key="s.value")
      b-card.summary-card.text-center.h-100(:border-variant="s.variant")
        .summary-count(:class="'text-' + s.variant") {{ s.count }}
        .summary-label.text-muted {{ s.label }}

  //- Filters
  b-card.mb-3.filters-card
    b-row
      b-col(md="4")
        b-form-group(label="Status")
          b-form-select(v-model="statusFilter" :options="statusOptions" @change="currentPage = 1")
      b-col(md="8")
        b-form-group(label="Search")
          b-input-group
            b-form-input(v-model="searchQuery" placeholder="Client, caregiver or CARA ID..." @input="currentPage = 1")
            b-input-group-append
              b-button(variant="outline-secondary" @click="searchQuery = ''")
                Icon(icon="tabler:x")
    b-row
      b-col(md="4")
        b-form-group(label="Month")
          b-form-input(type="month" v-model="monthFilter" @change="onMonthChange")
      b-col(md="4")
        b-form-group(label="From date")
          b-form-input(type="date" v-model="dateFrom" @change="onDateRangeChange")
      b-col(md="4")
        b-form-group(label="To date")
          b-form-input(type="date" v-model="dateTo" @change="onDateRangeChange")
    b-row(v-if="dateFrom || dateTo || monthFilter")
      b-col
        span.text-muted.small {{ dateRangeLabel }}
        b-button.ml-2.p-0(variant="link" size="sm" @click="clearDateFilters")
          Icon(icon="tabler:x")
          |  Clear

  //- Loading
  .text-center.py-5(v-if="loading")
    b-spinner(variant="primary")
    p.mt-3.text-muted Loading CARA records...

  //- Error
  b-alert(v-else-if="error" show variant="danger") {{ error }}

  //- Table
  div(v-else)
    .d-flex.justify-content-between.align-items-center.mb-2
      span.text-muted {{ filteredRecords.length }} record(s)
    b-table(
      :items="paginatedRecords"
      :fields="tableFields"
      striped
      hover
      responsive
      show-empty
      empty-text="No CARA records found."
    )
      template(#cell(client)="data")
        strong {{ data.item.clientName || 'N/A' }}
      template(#cell(caregiverName)="data")
        span {{ data.item.caregiverName || '—' }}
      template(#cell(statusLabel)="data")
        b-badge(:variant="statusVariant(data.item.status)") {{ data.item.statusLabel || statusText(data.item.status) }}
      template(#cell(caraId)="data")
        span(v-if="data.item.caraId") {{ data.item.caraId }}
        span.text-muted(v-else) —
      template(#cell(remark)="data")
        span(v-if="data.item.remark") {{ data.item.remark }}
        span.text-muted(v-else) —
      template(#cell(createdOn)="data")
        | {{ formatDate(data.item.createdOn) }}
      template(#cell(actions)="data")
        b-button(
          v-if="data.item.status === STATUS_NOT_INTERESTED"
          size="sm"
          variant="outline-info"
          :disabled="reactivatingId === data.item.id"
          @click="reactivate(data.item)"
        )
          b-spinner(small v-if="reactivatingId === data.item.id")
          Icon(v-else icon="tabler:arrow-back-up")
          |  Undo
        span.text-muted(v-else) —

    b-pagination.mt-3(
      v-if="filteredRecords.length > perPage"
      v-model="currentPage"
      :total-rows="filteredRecords.length"
      :per-page="perPage"
      align="center"
    )
</template>

<script>
import { Icon } from '@iconify/vue2';
import axios from 'axios';
import dayjs from 'dayjs';
import { exportCaraTemplate, exportStyledSheet } from '@/utils/caraExport';
import focusRefresh from '@/mixins/focusRefresh';

// crb5c_status choice values (must match the caregiver app's config.mjs / Dataverse).
const STATUS = {
  REGISTERED: 100000000,
  COMPLETED: 100000001,
  UPDATED: 100000002,
  NOT_INTERESTED: 100000003,
};

export default {
  name: 'CaraPage',
  components: { Icon },
  mixins: [focusRefresh],
  data() {
    return {
      records: [],
      loading: false,
      exporting: false,
      exportingStatuses: false,
      error: null,
      searchQuery: '',
      statusFilter: null,
      dateFrom: null,
      dateTo: null,
      monthFilter: null,
      currentPage: 1,
      perPage: 10,
      statusOptions: [
        { value: null, text: 'All Statuses' },
        { value: STATUS.REGISTERED, text: 'Registered' },
        { value: STATUS.COMPLETED, text: 'Completed' },
        { value: STATUS.UPDATED, text: 'Updated' },
        { value: STATUS.NOT_INTERESTED, text: 'Not Interested' },
      ],
      tableFields: [
        { key: 'client', label: 'Client', sortable: false },
        { key: 'caregiverName', label: 'Caregiver', sortable: false },
        { key: 'statusLabel', label: 'Status', sortable: false },
        { key: 'caraId', label: 'CARA ID', sortable: false },
        { key: 'remark', label: 'Remark', sortable: false },
        { key: 'createdOn', label: 'Date', sortable: true },
        { key: 'actions', label: '', sortable: false },
      ],
      reactivatingId: null,
    };
  },
  computed: {
    STATUS_NOT_INTERESTED() {
      return STATUS.NOT_INTERESTED;
    },
    filteredRecords() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.records.filter((r) => {
        if (this.statusFilter !== null && r.status !== this.statusFilter) return false;
        if (!this.inDateRange(r.createdOn)) return false;
        if (q) {
          const hay = `${r.clientName || ''} ${r.caregiverName || ''} ${r.caraId || ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
    },
    dateRangeLabel() {
      if (this.monthFilter) return `Showing ${dayjs(`${this.monthFilter}-01`).format('MMMM YYYY')}`;
      const from = this.dateFrom ? dayjs(this.dateFrom).format('DD MMM YYYY') : null;
      const to = this.dateTo ? dayjs(this.dateTo).format('DD MMM YYYY') : null;
      if (from && to) return `Showing ${from} – ${to}`;
      if (from) return `Showing from ${from}`;
      if (to) return `Showing until ${to}`;
      return '';
    },
    paginatedRecords() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRecords.slice(start, start + this.perPage);
    },
    statusSummary() {
      const count = (v) => this.records.filter((r) => r.status === v).length;
      return [
        { value: STATUS.REGISTERED, label: 'Registered', variant: 'info', count: count(STATUS.REGISTERED) },
        { value: STATUS.COMPLETED, label: 'Completed', variant: 'success', count: count(STATUS.COMPLETED) },
        { value: STATUS.UPDATED, label: 'Updated', variant: 'primary', count: count(STATUS.UPDATED) },
        { value: STATUS.NOT_INTERESTED, label: 'Not Interested', variant: 'secondary', count: count(STATUS.NOT_INTERESTED) },
      ];
    },
  },
  mounted() {
    this.fetchRecords();
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/cara-tracking');
        this.records = data.data || [];
      } catch (err) {
        console.error('Error fetching CARA records', err);
        this.error = 'Could not load CARA records. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    // Called by the focusRefresh mixin when the user returns to the tab/window.
    onFocusRefresh() {
      if (!this.loading) this.fetchRecords();
    },
    statusText(value) {
      switch (value) {
        case STATUS.REGISTERED: return 'Registered';
        case STATUS.COMPLETED: return 'Completed';
        case STATUS.UPDATED: return 'Updated';
        case STATUS.NOT_INTERESTED: return 'Not Interested';
        default: return 'Unknown';
      }
    },
    statusVariant(value) {
      switch (value) {
        case STATUS.REGISTERED: return 'info';
        case STATUS.COMPLETED: return 'success';
        case STATUS.UPDATED: return 'primary';
        case STATUS.NOT_INTERESTED: return 'secondary';
        default: return 'light';
      }
    },
    formatDate(value) {
      return value ? dayjs(value).format('DD MMM YYYY, h:mm A') : '—';
    },
    // Picking a month fills the from/to range with that month's bounds.
    onMonthChange() {
      this.currentPage = 1;
      if (this.monthFilter) {
        const m = dayjs(`${this.monthFilter}-01`);
        this.dateFrom = m.startOf('month').format('YYYY-MM-DD');
        this.dateTo = m.endOf('month').format('YYYY-MM-DD');
      } else {
        this.dateFrom = null;
        this.dateTo = null;
      }
    },
    // Manually editing the range invalidates the month shortcut.
    onDateRangeChange() {
      this.currentPage = 1;
      this.monthFilter = null;
    },
    clearDateFilters() {
      this.dateFrom = null;
      this.dateTo = null;
      this.monthFilter = null;
      this.currentPage = 1;
    },
    // Shared by the table filter and the export so both honour the same range.
    inDateRange(value) {
      if (!this.dateFrom && !this.dateTo) return true;
      if (!value) return false;
      const d = dayjs(value);
      if (this.dateFrom && d.isBefore(dayjs(this.dateFrom).startOf('day'))) return false;
      if (this.dateTo && d.isAfter(dayjs(this.dateTo).endOf('day'))) return false;
      return true;
    },
    exportFileName(base = 'CARA_Account_Creations') {
      if (this.monthFilter) return `${base}_${this.monthFilter}.xlsx`;
      if (this.dateFrom && this.dateTo) return `${base}_${this.dateFrom}_to_${this.dateTo}.xlsx`;
      if (this.dateFrom) return `${base}_from_${this.dateFrom}.xlsx`;
      if (this.dateTo) return `${base}_until_${this.dateTo}.xlsx`;
      return `${base}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
    },
    // Exports the styled two-sheet CARA workbook (PLWD Details + Caregiver Details),
    // matching the modular-form app. Data is the CARA form answers (crb5c_fowformanswers),
    // not the tracking table shown above, so we fetch it fresh on demand.
    async exportExcel() {
      this.exporting = true;
      try {
        const { data } = await axios.get('/api/cara-tracking/form-answers');
        let responses = data.data || [];
        // Honour the on-screen month / date-range selection in the export.
        if (this.dateFrom || this.dateTo) {
          responses = responses.filter((r) => this.inDateRange(r.createdon));
        }
        if (!responses.length) {
          const msg = (this.dateFrom || this.dateTo)
            ? 'No CARA form responses in the selected date range.'
            : 'No CARA form responses to export.';
          this.$bvToast
            ? this.$bvToast.toast(msg, { variant: 'warning', noCloseButton: true })
            : alert(msg);
          return;
        }
        exportCaraTemplate(responses, this.exportFileName());
      } catch (err) {
        console.error('CARA export failed', err);
        this.$bvToast
          ? this.$bvToast.toast('Export failed. Please try again.', { variant: 'danger', noCloseButton: true })
          : alert('Export failed. Please try again.');
      } finally {
        this.exporting = false;
      }
    },
    // Exports the CARA tracking statuses shown in the table — respects the
    // active Status / Search / date filters, since it uses filteredRecords.
    exportStatuses() {
      this.exportingStatuses = true;
      try {
        const records = this.filteredRecords;
        if (!records.length) {
          this.$bvToast
            ? this.$bvToast.toast('No CARA records to export for the current filters.', { variant: 'warning', noCloseButton: true })
            : alert('No CARA records to export for the current filters.');
          return;
        }
        const headers = ['Client', 'Caregiver', 'Status', 'CARA ID', 'Previous CARA ID', 'Remark', 'Date'];
        const rows = records.map((r) => ({
          Client: r.clientName || '',
          Caregiver: r.caregiverName || '',
          Status: r.statusLabel || this.statusText(r.status),
          'CARA ID': r.caraId || '',
          'Previous CARA ID': r.previousCaraId || '',
          Remark: r.remark || '',
          Date: r.createdOn ? dayjs(r.createdOn).format('DD MMM YYYY, h:mm A') : '',
        }));
        exportStyledSheet(rows, headers, {
          sheetName: 'CARA Tracking',
          fileName: this.exportFileName('CARA_Status_Tracking'),
        });
      } catch (err) {
        console.error('CARA status export failed', err);
        this.$bvToast
          ? this.$bvToast.toast('Export failed. Please try again.', { variant: 'danger', noCloseButton: true })
          : alert('Export failed. Please try again.');
      } finally {
        this.exportingStatuses = false;
      }
    },
    // Reverts a "Not Interested" record back to "Registered" so the client can
    // be picked up again if they change their mind later.
    async reactivate(item) {
      const name = item.clientName || 'this client';
      const message = `Reactivate ${name}? Their status will change from "Not Interested" back to "Registered".`;
      const confirmed = this.$bvModal
        ? await this.$bvModal.msgBoxConfirm(message, {
          title: 'Undo Not Interested',
          okVariant: 'info',
          okTitle: 'Reactivate',
          cancelTitle: 'Cancel',
          centered: true,
        })
        : window.confirm(message);
      if (!confirmed) return;

      this.reactivatingId = item.id;
      try {
        await axios.patch(`/api/cara-tracking/${item.id}/status`, { status: STATUS.REGISTERED });
        item.status = STATUS.REGISTERED;
        item.statusLabel = this.statusText(STATUS.REGISTERED);
        this.$bvToast && this.$bvToast.toast(`${name} reactivated as Registered.`, { variant: 'success', noCloseButton: true });
      } catch (err) {
        console.error('CARA reactivate failed', err);
        this.$bvToast
          ? this.$bvToast.toast('Could not update status. Please try again.', { variant: 'danger', noCloseButton: true })
          : alert('Could not update status. Please try again.');
      } finally {
        this.reactivatingId = null;
      }
    },
  },
};
</script>

<style scoped>
.cara-page {
  padding: 1rem;
}
.page-title {
  display: flex;
  align-items: center;
  font-weight: 600;
}
.summary-card {
  border-width: 1px;
}
.summary-count {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
}
.summary-label {
  font-size: 0.8rem;
}
.filters-card {
  background-color: #fafafa;
}
</style>
